import { useRef, useState } from 'react';
import axios from 'axios';
import {
  Upload, CheckCircle, AlertCircle, Loader, FileSpreadsheet, Tag, LayoutDashboard, ArrowUpRight,
} from 'lucide-react';
import KioskAnalytics from './KioskAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACCEPTED_EXT = /\.(csv|xlsx|xls)$/i;

const StatPill = ({ label, value }) => (
  <div className="flex flex-col items-start bg-white border border-green-200 rounded-lg px-3 py-2 min-w-[110px]">
    <span className="text-[10px] font-bold uppercase tracking-widest text-green-700">
      {label}
    </span>
    <span className="text-lg font-bold text-green-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {value}
    </span>
  </div>
);

const AdminScreen = () => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const acceptFile = (f) => {
    if (!f) return;
    if (!ACCEPTED_EXT.test(f.name)) {
      setError('Please select a CSV or XLSX file');
      setFile(null);
      return;
    }
    setError(null);
    setResult(null);
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    acceptFile(e.dataTransfer?.files?.[0]);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }
    try {
      setUploading(true);
      setError(null);
      setResult(null);
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/admin/upload-products`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(response.data);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const total = result?.products_count || 0;
  const cov = result?.coverage || {};

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <a
        data-testid="open-wellness-admin-dashboard-link"
        href="/wellness-admin"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-between bg-[#1f3d2b] hover:bg-[#16301f] text-white rounded-xl p-5 transition-colors group"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
            <LayoutDashboard size={18} />
          </div>
          <div>
            <p className="font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Wellness Club Admin Dashboard</p>
            <p className="text-sm text-white/70">Manage bookings, pricing, memberships, credits &amp; revenue</p>
          </div>
        </div>
        <ArrowUpRight size={20} className="text-[#d97736] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </a>

      <KioskAnalytics />

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Admin Panel
        </h2>
        <p className="text-gray-600">
          Drag &amp; drop your Vend / Lightspeed product export. The server will
          filter out non-supplements, drop inactive rows, and auto-tag every
          product with function, form, lifestyle, and condition tags.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Update Product Inventory
        </h3>

        {/* Drag-and-drop area */}
        <div
          data-testid="dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-10 text-center mb-6 cursor-pointer transition-colors ${
            dragOver
              ? 'border-[#0066FF] bg-[#0066FF]/5'
              : 'border-gray-300 hover:border-[#0066FF]'
          }`}
        >
          <div className="flex flex-col items-center">
            <FileSpreadsheet className="text-gray-400 mb-3" size={44} />
            <p className="text-base font-semibold text-gray-800 mb-1">
              Drop CSV or XLSX file here
            </p>
            <p className="text-sm text-gray-500 mb-4">or click to browse</p>
            <input
              ref={inputRef}
              data-testid="csv-file-input"
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => acceptFile(e.target.files?.[0])}
              className="hidden"
            />
            {file && (
              <p className="text-sm text-gray-700">
                <span className="font-semibold">{file.name}</span>
                <span className="text-gray-500 ml-2">({(file.size / 1024).toFixed(1)} KB)</span>
              </p>
            )}
          </div>
        </div>

        <button
          data-testid="upload-button"
          onClick={handleUpload}
          disabled={!file || uploading}
          className="w-full bg-[#0066FF] hover:bg-[#0052CC] text-white font-semibold rounded-lg h-14 px-8 text-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {uploading ? (
            <><Loader className="animate-spin" size={20} /> Filtering &amp; tagging…</>
          ) : (
            <><Upload size={20} /> Upload, Filter &amp; Auto-Tag</>
          )}
        </button>

        {/* Success */}
        {result && (
          <div data-testid="success-message" className="mt-6 bg-green-50 border border-green-200 rounded-xl p-5">
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={22} />
              <div className="flex-1">
                <h4 className="font-bold text-green-900 mb-1">
                  {total.toLocaleString()} products imported &amp; tagged
                </h4>
                {result.stats && (
                  <p className="text-sm text-green-800">
                    Filtered out {result.stats.inactive} inactive,{' '}
                    {result.stats.non_supplement} non-supplement,{' '}
                    {result.stats.duplicate_sku} duplicate, and{' '}
                    {result.stats.blank_sku} blank-SKU rows.
                  </p>
                )}
              </div>
            </div>

            {/* Tag coverage stats */}
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-green-700 mb-2">
              <Tag size={12} /> Auto-tag coverage
            </div>
            <div className="flex flex-wrap gap-2">
              <StatPill label="function" value={`${cov.function_tags ?? 0}`} />
              <StatPill label="form" value={`${cov.form_tags ?? 0}`} />
              <StatPill label="lifestyle" value={`${cov.lifestyle_tags ?? 0}`} />
              <StatPill label="condition" value={`${cov.condition_tags ?? 0}`} />
            </div>

            {result.sample_products?.length > 0 && (
              <div className="mt-4 text-xs text-green-700">
                <p className="font-semibold mb-1">Sample:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {result.sample_products.slice(0, 3).map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div data-testid="error-message" className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <h4 className="font-semibold text-red-900 mb-1">Upload Failed</h4>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}
      </div>

      {/* Help */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
          What gets tagged automatically
        </h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p>The server reads your file as-is — no formatting required. SKU/UPC values are kept byte-identical (text, no rounding).</p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li><strong>function_tags</strong> — muscle, energy, sleep, stress, immunity, digestion, joint, heart, cognitive, beauty, …</li>
            <li><strong>form_tags</strong> — capsules, tablets, softgels, powder, liquid, gummies, drops, spray, topical, tea, lozenge</li>
            <li><strong>lifestyle_tags</strong> — vegan, organic, non-GMO, gluten-free, kids, men's, women's, prenatal, senior, athlete, keto</li>
            <li><strong>condition_tags</strong> — cold/flu, allergies, insomnia, menopause, prostate, blood sugar, joint pain, gut health, …</li>
          </ul>
          <p className="mt-3"><strong>Excluded automatically:</strong> books, shipping, gift cards, paper / kitchen-waste bags, donations, rentals.</p>
          <p className="text-xs text-blue-700 mt-3">Accepted: <code>.csv</code>, <code>.xlsx</code>, <code>.xls</code> · max 25 MB</p>
        </div>
      </div>
    </div>
  );
};

export default AdminScreen;
